import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import UnifiedNavHeader from "../../../../components/navigation/UnifiedNavHeader";
import { Header } from "../../../../components/dashboards/components/shared/Header";
import { MMA_HEADER_COPY } from "../constants/headerContent";
import MMADesktopFightCard from "../components/MMADesktopFightCard";
import MMAPredictionControlBar from "../MMAPredictionControlBar";
import mmaService from "../../../../components/dashboards/services/mmaService";
import DesktopRailNavigation from "../components/desktop/DesktopRailNavigation";
import useMMARailFilters from "../components/MMARailFilters";
import { useQuickPrediction } from "../../../../components/dashboards/hooks/useQuickPrediction";
import { getBrazilianStatus } from "../../../../utils/detectBrazil";
import { mmaMobilePtBR } from "../../../../components/mappings/sports-dash-mappings/mma-mobile.pt-br";
import { DatePickerModal } from "../../../../components/dashboards/components/shared/date-picker";
import { ensureISODate } from "../../../../components/dashboards/utils/dateUtils";

const DEFAULT_PAGINATION = { currentPage: 1, perPage: 8, totalPages: 1 };

const MMADesktopDashboard = () => {
  const isBrazilian = getBrazilianStatus();
  const t = useCallback(
    (text) => (isBrazilian ? mmaMobilePtBR[text] || text : text),
    [isBrazilian]
  );

  const [filters, setFilters] = useState({ date: new Date().toISOString().split('T')[0], category: '' });
  const [fights, setFights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [selectedFight, setSelectedFight] = useState(null);
  const [predictionRequestId, setPredictionRequestId] = useState(0);
  const activePredictionRequestRef = useRef(null);
  const [predictionFlowActive, setPredictionFlowActive] = useState(false);
  const cacheCheckKeyRef = useRef(null);

  // Prediction hook
  const {
    isLoading: isPredicting,
    selectedGames: selectedFightsForPrediction,
    usageState,
    error: predictionError,
    refundNotice,
    toggleGameSelection,
    clearSelections,
    predictSelectedGames,
    getGamePrediction,
    isGameSelected,
    selectionCount,
    isGameFrozen,
    checkCachedPredictions
  } = useQuickPrediction();

  // Handle rail filter changes
  const handleRailFiltersChange = useCallback((nextFilters) => {
    setFilters(prev => ({
      ...prev,
      date: ensureISODate(nextFilters.date, prev.date)
    }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, []);

  // Initialize rail filters hook
  const {
    filters: railFilters,
    activeTab: railActiveTab,
    hasActiveFilters: railHasActiveFilters,
    filterObjects: railFilterObjects,
    handleTabChange: handleRailTabChange,
    handleFilterClick,
    updateFilter: updateRailFilter,
    resetFilters: resetRailFilters,
    showDateSheet: showDateModal,
    setShowDateSheet: setShowDateModal
  } = useMMARailFilters({
    initialDate: filters.date,
    onFiltersChange: handleRailFiltersChange
  });

  const handlePredictRequest = useCallback(async () => {
    setPredictionRequestId((prev) => {
      const next = prev + 1;
      activePredictionRequestRef.current = next;
      return next;
    });
    setPredictionFlowActive(true);
    await predictSelectedGames("mma");
    clearSelections();
    setPredictionFlowActive(false);
  }, [clearSelections, predictSelectedGames]);

  const loadFights = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await mmaService.makeRequest('/fights', { date: ensureISODate(filters.date), category: filters.category || undefined });
      const list = data?.response || data?.fights || [];
      setFights(list);
      setPagination(prev => ({
        ...prev,
        totalPages: Math.max(1, Math.ceil(list.length / prev.perPage)),
        currentPage: 1
      }));
    } catch (e) {
      setError('Failed to load fights');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadFights();
  }, [loadFights]);

  // Prime predictions from local cache / Firestore when fights load
  useEffect(() => {
    if (!fights.length) return;
    const fightIds = fights.map((f) => f.id).filter(Boolean);
    if (!fightIds.length) return;
    const key = fightIds.slice().sort().join(",");
    if (cacheCheckKeyRef.current === key) return;
    cacheCheckKeyRef.current = key;

    checkCachedPredictions?.("mma", fightIds).catch((err) =>
      console.error("checkCachedPredictions failed (MMA desktop):", err)
    );
  }, [fights, checkCachedPredictions]);

  // Auto-reset selections when a prediction request finishes
  useEffect(() => {
    if (isPredicting) return;

    const shouldFinalize =
      !isPredicting &&
      !predictionFlowActive &&
      activePredictionRequestRef.current === predictionRequestId &&
      predictionRequestId > 0;

    if (shouldFinalize) {
      clearSelections();
      activePredictionRequestRef.current = null;
      setTimeout(() => clearSelections(), 600);
    }
  }, [clearSelections, isPredicting, predictionFlowActive, predictionRequestId]);

  const pageFights = useMemo(() => {
    const start = (pagination.currentPage - 1) * pagination.perPage;
    return fights.slice(start, start + pagination.perPage);
  }, [fights, pagination]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: key === "date" ? ensureISODate(value, prev.date) : value }));
  };

  const formatTime = useCallback((value) => {
    if (!value) return '';
    const d = new Date(value);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <UnifiedNavHeader title="MMA Dashboard" showBackButton />
      <div className="relative px-6 py-8 pb-24">
        <div className="max-w-[1600px] mx-auto space-y-4">
          <Header t={t} {...MMA_HEADER_COPY} />

          {/* Desktop Rail Navigation - Filter Rail */}
          <DesktopRailNavigation
            sport="mma"
            primaryTabs={[{ id: 'all', label: 'All Fights' }]}
            filters={railFilterObjects}
            activeTab={railActiveTab}
            onTabChange={handleRailTabChange}
            onFilterChange={handleFilterClick}
            onReset={resetRailFilters}
            hasActiveFilters={railHasActiveFilters}
          />

          <div className="space-y-6 border border-white/10 rounded-sm bg-white/[0.02]">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <div>
                <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-gray-200">Games</p>
                <h2 className="text-2xl font-black uppercase tracking-tight">Fights ({fights.length})</h2>
              </div>
              <span className="text-[11px] font-mono uppercase tracking-[0.3em] text-gray-500">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
            </div>

            <div className="p-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {loading ? (
                <div className="text-white">Loading...</div>
              ) : (
                pageFights.map((fight, idx) => (
                  <MMADesktopFightCard
                    key={fight?.id || idx}
                    fight={fight}
                    isSelected={isGameSelected(fight)}
                    isFrozen={isGameFrozen(fight.id)}
                    onToggleSelection={toggleGameSelection}
                    prediction={getGamePrediction(fight.id)}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Filter Modal */}
      <DatePickerModal
        isOpen={showDateModal}
        onClose={() => setShowDateModal(false)}
        selectedDate={ensureISODate(railFilters.date || filters.date)}
        onDateSelect={(isoDate) => {
          const formatted = ensureISODate(isoDate, filters.date);
          updateRailFilter("date", formatted);
          setFilters((prev) => ({ ...prev, date: formatted }));
          setShowDateModal(false);
        }}
      />

      {/* Prediction Control Bar */}
      <MMAPredictionControlBar
        selectionCount={selectionCount}
        usageState={usageState}
        isLoading={isPredicting}
        error={predictionError}
        refundNotice={refundNotice}
        onPredict={handlePredictRequest}
        onClear={clearSelections}
        selectedFights={selectedFightsForPrediction || []}
      />
    </div>
  );
};

export default MMADesktopDashboard;
